import { ApiConstant, AppConstant, KeyConstant, SystemConstant } from "const";
import { call, put, select } from "redux-saga/effects";
import { RestoreActions } from "redux-store/restore.redux";
import { getInteractor } from "services/local.service";
import { AttachmentUtil, CryptoUtil, MultipleFileUtil, toCamel, toSnake, uuid, StorageUtil } from "utils";
import { remoteApiFactory } from "services";
import { getAllDeviceFromRemote } from "pubsub/services/device.service";
import store, { ConversationActions } from "redux-store";
import { uploadFile } from "services/multiple-file";
import { getFileList, updateFileStatus } from "pubsub/services/file.service";
import { getFileLocalPath } from "services/attachment.service";

const EncryptTypeProtocol = window.bglobalSignal.libs.EncryptTypeProtocol;

export function* changeDeviceRole(action) {
  const prefixKey = action?.prefixKey || StorageUtil.getCurrentPrefixKey();

  try {
    const response = yield call(remoteApiFactory.getBranchApi(prefixKey).updateDevice, { change_master_f: 1 });
    if (response.status === ApiConstant.STT_OK) {
      yield put(
        RestoreActions.restoreSet({
          changeRoleStatus: SystemConstant.REDUX_LIFECYCLE.success,
        }),
      );
    } else {
      yield put(
        RestoreActions.restoreSet({
          changeRoleStatus: SystemConstant.REDUX_LIFECYCLE.fail,
        }),
      );
    }
  } catch (error) {
    console.error(error);
    RestoreActions.restoreSet({
      changeRoleStatus: SystemConstant.REDUX_LIFECYCLE.fail,
    });
  }
}

export function* verifyChangeDeviceRole(action) {
  try {
    const { data } = action;
    const prefixKey = action?.prefixKey || StorageUtil.getCurrentPrefixKey();

    const response = yield call(remoteApiFactory.getBranchApi(prefixKey).requestVerifyChangeDeviceRole, data);
    if (response.status === ApiConstant.STT_OK) {
      yield put(
        RestoreActions.restoreSet({
          verifyStatus: AppConstant.VERIFY_OTP_ERROR_TYPE.success,
        }),
      );

      yield getAllDeviceFromRemote(prefixKey);

      StorageUtil.setItem(KeyConstant.KEY_VERIFY_RETRIES, 0, prefixKey);
    } else if (response.status === ApiConstant.STT_FORBIDDEN) {
      yield put(
        RestoreActions.restoreSet({
          verifyStatus: AppConstant.VERIFY_OTP_ERROR_TYPE.limitResend,
        }),
      );
    } else if (response.status === ApiConstant.STT_BAD_REQUEST) {
      yield put(
        RestoreActions.restoreSet({
          verifyStatus: AppConstant.VERIFY_OTP_ERROR_TYPE.wrongOtp,
        }),
      );
    } else if (response.status === ApiConstant.STT_OTP_EXPIRED) {
      yield put(
        RestoreActions.restoreSet({
          verifyStatus: AppConstant.VERIFY_OTP_ERROR_TYPE.expiredOtp,
        }),
      );
    } else {
      yield put(
        RestoreActions.restoreSet({
          verifyStatus: AppConstant.VERIFY_OTP_ERROR_TYPE.systemError,
        }),
      );
    }
  } catch (error) {
    console.error(error);
    yield put(
      RestoreActions.restoreSet({
        verifyStatus: AppConstant.VERIFY_OTP_ERROR_TYPE.systemError,
      }),
    );
  }
}

// Get file info
export function* restore(action) {
  try {
    const { data } = action;
    const prefixKey = action?.prefixKey || StorageUtil.getCurrentPrefixKey();

    const response = yield call(remoteApiFactory.getBranchApi(prefixKey).requestGetBackup, toSnake(data));
    if (response.status === ApiConstant.STT_OK) {
      const backupInfo = toCamel(response.data);
      if (backupInfo && Object.keys(backupInfo).length > 0) {
        yield put(
          RestoreActions.restoreSet({
            backupInfo: backupInfo,
            restoreStatus: SystemConstant.RESTORE_STATUS.getFileInfo,
          }),
        );
      } else {
        yield put(
          RestoreActions.restoreSet({
            backupInfo: backupInfo,
            restoreStatus: SystemConstant.RESTORE_STATUS.noBackupFile,
          }),
        );
      }
    } else {
      throw new Error("An error occurred");
    }
  } catch (error) {
    console.error(error);
    yield put(
      RestoreActions.restoreSet({
        backupInfo: null,
        restoreStatus: SystemConstant.RESTORE_STATUS.error,
      }),
    );
  }
}

/**
 *
 * Check passCode
 * Get file
 * Decrypt data and migrate data
 */
export function* restoreToLocal(action) {
  try {
    const { passCode } = action.data;
    const prefixKey = action?.prefixKey || StorageUtil.getCurrentPrefixKey();
    const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
    const deviceId = StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey);

    const {
      restoreRedux: { backupInfo },
    } = yield select();

    if (Boolean(backupInfo)) {
      yield put(
        RestoreActions.restoreSet({
          restoreStatus: SystemConstant.RESTORE_STATUS.inProgress,
        }),
      );

      const key0 = "" + passCode + accountId + backupInfo.createdTime;
      const key0Encrypt = yield CryptoUtil.encryptHmac256(key0);
      const keyEncryptFileInfo = yield CryptoUtil.decryptCBC(backupInfo.ckx, key0Encrypt);
      const aesKeyInfo = JSON.parse(keyEncryptFileInfo);

      const fileName = `${backupInfo.attachmentId}.txt`;
      const backupFile = {
        attachmentId: backupInfo.attachmentId,
        metaData: { fileName },
        aesKeyInfo: aesKeyInfo,
      };

      const filePath = yield getFileLocalPath(prefixKey, backupFile);

      if (filePath) {
        const fileLocal = yield getInteractor(prefixKey).LocalFileService.get(backupInfo.attachmentId);
        const fileUrl = AttachmentUtil.getLocalPath(fileLocal.url, fileName);
        yield getInteractor(prefixKey).LocalRestoreService.restoreFromFile(fileUrl, deviceId);

        yield restoreThread(prefixKey, accountId);

        // Get file record
        const attachmentIds = (yield getInteractor(prefixKey).LocalFileGroupService.getAllFileIds()).map(
          item => item.file_id,
        );

        if (Array.isArray(attachmentIds) && attachmentIds.length > 0) {
          let reqAttachment = [];
          let offset = 0;
          let lastIndex = 100;
          do {
            reqAttachment = attachmentIds.slice(offset, lastIndex);
            yield getFileList(prefixKey, reqAttachment);
            offset = lastIndex;
            lastIndex += 100;
          } while (reqAttachment.length === 100);
        }

        const minDisappearTime = yield getInteractor(prefixKey).LocalMessageService.getMinDisappearTime();
        if (minDisappearTime > 0) {
          yield put(
            ConversationActions.conversationSet({
              disappearTime: minDisappearTime,
            }),
          );
        }

        yield put(
          RestoreActions.restoreSet({
            restoreStatus: SystemConstant.RESTORE_STATUS.success,
          }),
        );

        // Trigger update UI
        yield put(
          ConversationActions.conversationSet({
            isUpdateViewMode: Date.now(),
          }),
        );
      } else {
        throw new Error("RESTORE - FILE NOT FOUND");
      }
    }
  } catch (error) {
    console.error(error);
    if (error.message === AppConstant.PASSCODE_ERROR) {
      yield put(
        RestoreActions.restoreSet({
          restoreStatus: SystemConstant.RESTORE_STATUS.wrongPasscode,
        }),
      );
    } else {
      yield put(
        RestoreActions.restoreSet({
          restoreStatus: SystemConstant.RESTORE_STATUS.error,
        }),
      );
    }
  }
}

// Upload file backup to server
export function* backup(action) {
  try {
    const prefixKey = action?.prefixKey || StorageUtil.getCurrentPrefixKey();
    const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
    const deviceId = StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey);
    const { passCode, createdTime } = action.data;

    yield put(
      RestoreActions.backupSet({
        backupStatus: SystemConstant.RESTORE_STATUS.inProgress,
      }),
    );
    const file = yield getInteractor(prefixKey).LocalMessageService.getMessagesAndCallHistories(deviceId);
    const countOldVersionDevice = yield getInteractor(prefixKey).LocalDeviceService.countOldVersion(
      {
        androidVersion: SystemConstant.VERSION_IVS.android,
        iosVersion: SystemConstant.VERSION_IVS.ios,
        desktopVersion: SystemConstant.VERSION_IVS.desktop,
      },
      { account_id: accountId },
    );

    if (file) {
      const attachmentId = uuid();
      const iv = CryptoUtil.randomBytes(16);
      const keyEncryptFile = CryptoUtil.randomBytes(32);
      const fileEncryptionParams = { encryptTypeProtocol: EncryptTypeProtocol.AES };
      const fileEncrypt =
        countOldVersionDevice > 0
          ? yield MultipleFileUtil.encryptFile(attachmentId, file, keyEncryptFile, iv, fileEncryptionParams)
          : yield MultipleFileUtil.encryptFile_v2(attachmentId, file, fileEncryptionParams);

      const aesKeyInfo = fileEncrypt.aes_key_info;
      const key = "" + passCode + accountId + createdTime;
      const keyEncrypt = yield CryptoUtil.encryptHmac256(key);

      const CKx = yield CryptoUtil.asyncEncryptCBC(JSON.stringify(aesKeyInfo), keyEncrypt);

      const backup2Trios = {
        attachment_id: attachmentId,
        ckx: CKx,
        created_time: createdTime,
      };

      // upload tus
      yield MultipleFileUtil.zipFile(attachmentId);
      uploadFile(prefixKey, attachmentId, SystemConstant.UPLOAD_TYPE.backup, null, {
        onError: () => handleBackupError(prefixKey, attachmentId),
        onSuccess: () => handleBackupSuccess(prefixKey, backup2Trios, file.size),
      });
    }
  } catch (error) {
    console.error("backup fail: ", error);
    yield put(
      RestoreActions.backupSet({
        backupStatus: SystemConstant.RESTORE_STATUS.error,
      }),
    );
  }
}

export const restoreThread = async (prefixKey, accountId) => {
  const threadIds = await getInteractor(prefixKey).LocalMessageService.getThreadInfoFromMsg();

  let restoreThreads = [];
  for (let index = 0; index < threadIds.length; index++) {
    const threadId = threadIds[index].thread_id;

    const parentMessage = await getInteractor(prefixKey).LocalMessageService.findOne({
      source_id: threadId,
      send_type: SystemConstant.NORMAL_MSG_SEND_TYPE,
      state: SystemConstant.STATE.active,
    });
    if (false === Boolean(parentMessage && parentMessage.id)) continue;

    const messagesInThread = await getInteractor(prefixKey).LocalMessageService.find({
      thread_id: threadId,
      state: SystemConstant.STATE.active,
    });
    if (false === (Array.isArray(messagesInThread) && messagesInThread.length > 0)) continue;

    const totalReply = await getInteractor(prefixKey).LocalMessageService.countTotalReplyInThread(threadId);
    const totalUnread = await getInteractor(prefixKey).LocalMessageService.countUnreadMessageInThread(
      accountId,
      threadId,
    );
    const isDelete = totalReply <= 0;
    const lastMessage = await getInteractor(prefixKey).LocalMessageService.getLastReplyMsg(threadId);
    const isMeReply =
      parentMessage.sender_id === accountId || messagesInThread.filter(item => item.sender_id === accountId).length > 0;

    const mentionArr = [parentMessage, ...messagesInThread].reduce((resultArr, item) => {
      if (Boolean(item.mentions)) {
        const mention = JSON.parse(item.mentions);
        return [...resultArr, ...mention];
      } else {
        return resultArr;
      }
    }, []);
    const isInvolved = isMeReply || mentionArr.includes(accountId);
    const accountReply = await getInteractor(prefixKey).LocalMessageService.getAccountReply(threadId);

    const newThreadInfo = {
      id: uuid(),
      thread_id: threadId,
      total_reply: totalReply,
      total_unread: totalUnread,
      involved_f: isInvolved ? SystemConstant.STATE.active : SystemConstant.STATE.inactive,
      thread_account_id: parentMessage.sender_id,
      thread_created: parentMessage.created,
      group_id: parentMessage.group_id,
      group_type: null,
      setting_type: null,
      thread_content: parentMessage.content, // TODO: original content of parentMessage
      thread_mentions: JSON.stringify(mentionArr),
      branch_id: parentMessage.branch_id,
      state: isDelete ? AppConstant.THREAD_STATE.inactive : AppConstant.THREAD_STATE.active,
      created: parentMessage.created,
      modified: lastMessage.created || 0,
      force_sync_f: 1,
      account_reply: JSON.stringify(accountReply || []),
    };

    restoreThreads.push(newThreadInfo);
  }

  await getInteractor(prefixKey).LocalThreadService.save(restoreThreads);
};

const handleBackupError = async (prefixKey, attachmentId) => {
  store.dispatch(
    RestoreActions.backupSet({
      backupStatus: SystemConstant.RESTORE_STATUS.error,
    }),
  );

  await updateFileStatus(prefixKey, attachmentId, SystemConstant.UPLOAD_STATUS.failed);
};

const handleBackupSuccess = async (prefixKey, backup2Trios, fileSize) => {
  const responseBackup = await remoteApiFactory.getBranchApi(prefixKey).requestBackup(backup2Trios);
  if (responseBackup.status === ApiConstant.STT_OK) {
    store.dispatch(
      RestoreActions.backupSet({
        fileSize: fileSize,
        backupStatus: SystemConstant.RESTORE_STATUS.success,
      }),
    );
  } else if (responseBackup.status !== ApiConstant.STT_FORBIDDEN) {
    store.dispatch(
      RestoreActions.backupSet({
        backupStatus: SystemConstant.RESTORE_STATUS.losePermission,
      }),
    );
  } else {
    store.dispatch(
      RestoreActions.backupSet({
        backupStatus: SystemConstant.RESTORE_STATUS.error,
      }),
    );
  }
};
